import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Papa from 'papaparse';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import { importStoneSizeCSV } from '../../../../api/masters/stoneAttribute/stoneSize/stoneSizeApi';

const StoneSizeForm = ({
  onSubmit, editingStoneSize, onCancel, formSuccess = '', formError = '', setFormSuccess, setFormError, companies = [], sieveSizes = [], users = [], checkCodeExists
}) => {
  const [form, setForm] = useState({ stone_size_code: '', stone_size_name: '', sieve_size: '', company_code: '', status: 'Active' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeExists, setCodeExists] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importedRows, setImportedRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccess, setImportSuccess] = useState('');
  const [importError, setImportError] = useState('');
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    if (editingStoneSize) setForm(editingStoneSize);
    else setForm({ stone_size_code: '', stone_size_name: '', sieve_size: '', company_code: '', status: 'Active' });
    setErrors({});
    setCodeExists(false);
    setFormSuccess && setFormSuccess('');
    setFormError && setFormError('');
  }, [editingStoneSize]);

  useEffect(() => {
    if (formSuccess) {
      const timer = setTimeout(() => setFormSuccess && setFormSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [formSuccess, setFormSuccess]);

  const isAlphanumeric = (str) => /^[A-Za-z0-9]+$/.test(str);

  const formatDate = (date) => date ? dayjs(date).format('DD-MM-YYYY hh:mm A') : 'Unknown';
  const getUserDisplay = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.user_id === userId);
    if (!user) return 'Unknown';
    return `${user.username} (${user.role_name || 'Unknown'})`;
  };

  const validate = async () => {
    const newErrors = {};
    const code = form.stone_size_code.trim();
    if (!form.company_code) newErrors.company_code = 'Please select a company code.';
    if (!code) newErrors.stone_size_code = 'Please enter a code.';
    else if (/[^A-Za-z0-9]/.test(code)) newErrors.stone_size_code = 'Code can only contain letters and numbers.';
    if (!form.stone_size_name.trim()) newErrors.stone_size_name = 'Please enter a stone size name.';
    
    // Only check code uniqueness during ADD operation (not during edit since code is disabled)
    if (!editingStoneSize && code && isAlphanumeric(code) && checkCodeExists) {
      setCodeChecking(true);
      const exists = await checkCodeExists(code);
      setCodeChecking(false);
      if (exists) newErrors.stone_size_code = 'This code already exists and cannot be reused.';
    }
    
    setErrors(newErrors);
    setFormError && setFormError('');
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'stone_size_code') {
      if (value && /[^A-Za-z0-9]/.test(value)) {
        setErrors(prev => ({ ...prev, [name]: 'Code can only contain letters and numbers.' }));
      } else {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      }
    } else {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    setCodeExists(false);
    setFormError && setFormError('');
    setFormSuccess && setFormSuccess('');
  };

  const handleBlur = async (e) => {
    if (e.target.name === 'stone_size_code') {
      const value = form.stone_size_code.trim();
      if (value && isAlphanumeric(value) && checkCodeExists) {
        setCodeChecking(true);
        const exists = await checkCodeExists(value, editingStoneSize?.id);
        setCodeChecking(false);
        if (exists) {
          setErrors(prev => ({ ...prev, stone_size_code: 'This code already exists and cannot be reused.' }));
          setCodeExists(true);
        } else {
          setErrors(prev => ({ ...prev, stone_size_code: undefined }));
          setCodeExists(false);
        }
      }
    }
  };

  const handleSieveSizeChange = (event, newValue) => {
    setForm(prev => ({
      ...prev,
      sieve_size: newValue ? newValue.sieve_size_code : ''
    }));
    setErrors(prev => ({ ...prev, sieve_size: undefined }));
    setFormError && setFormError('');
    setFormSuccess && setFormSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    const isValid = await validate();
    if (!isValid) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(form);
      if (!editingStoneSize) {
        setForm({ stone_size_code: '', stone_size_name: '', sieve_size: '', company_code: '', status: 'Active' });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setForm({ stone_size_code: '', stone_size_name: '', sieve_size: '', company_code: '', status: 'Active' });
    setErrors({});
    setCodeExists(false);
    setFormError && setFormError('');
    setFormSuccess && setFormSuccess('');
  };

  const handleDownloadTemplate = () => {
    const headers = ['stone_size_code', 'stone_size_name', 'sieve_size', 'company_code', 'status'];
    const csvContent = [headers.join(','), 'SS001,Small Size,2-3mm,COMP001,Active'].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stone_size_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        setImportedRows(results.data);
        setImportErrors([]);
        setImportSuccess('');
        setImportError('');
      },
      error: (error) => {
        setImportError('Error parsing CSV file: ' + error.message);
      }
    });
  };

  const handleConfirmImport = async () => {
    if (!importFile) return;
    setImportSuccess('');
    setImportError('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await importStoneSizeCSV(formData);
      setImportSuccess(`Imported: ${res.imported}, Failed: ${res.failed}`);
      setImportErrors(res.errors || []);
    } catch (err) {
      setImportError(err?.response?.data?.error || err.message);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400, width: '100%', mx: 'auto', mt: 1, p: 0 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudDownloadIcon />}
          onClick={handleDownloadTemplate}
          sx={{
            bgcolor: '#8e24aa',
            '&:hover': { bgcolor: '#6a1b9a' },
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 2,
            minWidth: 110,
            height: 36,
            fontSize: 12,
            letterSpacing: 1,
            boxShadow: 'none',
            px: 1.5,
          }}
        >
          Download Template
        </Button>
        <Button
          variant="contained"
          component="label"
          color="primary"
          startIcon={<UploadFileIcon />}
          sx={{
            bgcolor: '#00bcd4',
            '&:hover': { bgcolor: '#008ba3' },
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 2,
            minWidth: 110,
            height: 36,
            fontSize: 12,
            letterSpacing: 1,
            boxShadow: 'none',
            px: 1.5,
          }}
        >
          Import CSV
          <input type="file" accept=".csv" hidden onChange={handleImportCSV} />
        </Button>
      </Box>
      <Box sx={{ fontWeight: 700, fontSize: 18, color: '#111', mb: 2.5, letterSpacing: 0.7, textAlign: 'left', width: '100%' }}>
        {editingStoneSize ? 'Edit Stone Size' : 'Add Stone Size'}
      </Box>
      <Stack spacing={2} alignItems="center">
        <FormControl fullWidth error={!!errors.company_code} size="small">
          <Autocomplete
            id="company_code"
            options={companies}
            getOptionLabel={(option) => option.company_code && option.company_name ? `${option.company_code} - ${option.company_name}` : ''}
            value={companies.find(c => c.company_code === form.company_code) || null}
            onChange={(event, newValue) => {
              setForm({ ...form, company_code: newValue ? newValue.company_code : '' });
              setErrors(prev => ({ ...prev, company_code: undefined }));
              setFormError && setFormError('');
              setFormSuccess && setFormSuccess('');
            }}
            isOptionEqualToValue={(option, value) => option.company_code === value.company_code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Company Code"
                error={!!errors.company_code}
                helperText={errors.company_code}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 45,
                    height: 45,
                    color: '#111',
                    '& fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#BDBDBD' },
                    '&:hover fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#BDBDBD' },
                    '&.Mui-focused fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#00C853' },
                  },
                  '& label.Mui-focused': { color: '#00C853' },
                }}
                inputProps={{ ...params.inputProps, maxLength: 50 }}
              />
            )}
            filterOptions={(options, { inputValue }) =>
              options.filter(option => {
                const search = inputValue.toLowerCase();
                return (
                  (option.company_name && option.company_name.toLowerCase().includes(search)) ||
                  (option.company_code && option.company_code.toLowerCase().includes(search))
                );
              })
            }
            clearOnEscape
          />
        </FormControl>
        <TextField
          label="Stone Size Code*"
          name="stone_size_code"
          value={form.stone_size_code}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!errors.stone_size_code}
          helperText={errors.stone_size_code}
          fullWidth
          inputProps={{ maxLength: 20 }}
          disabled={!!editingStoneSize}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: !!errors.stone_size_code ? '#D32F2F' : '#BDBDBD' },
              '&:hover fieldset': { borderColor: !!errors.stone_size_code ? '#D32F2F' : '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: !!errors.stone_size_code ? '#D32F2F' : '#00C853' },
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
          InputProps={{
            sx: {
              background: '#fff',
              boxShadow: 'none',
              padding: 0,
              minHeight: 45,
              height: 45,
              '& input': {
                background: '#fff !important',
                boxShadow: 'none !important',
                padding: '0 8px',
                minHeight: 45,
                height: 45,
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitBackgroundClip: 'text',
              },
            },
          }}
        />
        <TextField
          label="Stone Size Name*"
          name="stone_size_name"
          value={form.stone_size_name}
          onChange={handleChange}
          error={!!errors.stone_size_name}
          helperText={errors.stone_size_name}
          fullWidth
          inputProps={{ maxLength: 50 }}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: !!errors.stone_size_name ? '#D32F2F' : '#BDBDBD' },
              '&:hover fieldset': { borderColor: !!errors.stone_size_name ? '#D32F2F' : '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: !!errors.stone_size_name ? '#D32F2F' : '#00C853' },
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
          InputProps={{
            sx: {
              background: '#fff',
              boxShadow: 'none',
              padding: 0,
              minHeight: 45,
              height: 45,
              '& input': {
                background: '#fff !important',
                boxShadow: 'none !important',
                padding: '0 8px',
                minHeight: 45,
                height: 45,
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitBackgroundClip: 'text',
              },
            },
          }}
        />
        <FormControl fullWidth size="small">
          <Autocomplete
            id="sieve_size"
            options={sieveSizes}
            getOptionLabel={option => option.sieve_size_code && option.sieve_size_name ? `${option.sieve_size_code} - ${option.sieve_size_name}` : ''}
            value={sieveSizes.find(s => s.sieve_size_code === form.sieve_size) || null}
            onChange={handleSieveSizeChange}
            isOptionEqualToValue={(option, value) => option.sieve_size_code === value.sieve_size_code}
            renderInput={params => (
              <TextField
                {...params}
                label="Sieve Size Name"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 45,
                    height: 45,
                    color: '#111',
                    '& fieldset': { borderColor: '#BDBDBD' },
                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                    '&.Mui-focused fieldset': { borderColor: '#00C853' },
                  },
                  mb: 1.5,
                  '& label.Mui-focused': { color: '#00C853' },
                }}
                inputProps={{ ...params.inputProps, maxLength: 50 }}
              />
            )}
            filterOptions={(options, { inputValue }) => {
              return options.filter(option =>
                option.sieve_size_code?.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.sieve_size_name?.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
            clearOnEscape
          />
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="status-label" sx={{ color: '#111', '&.Mui-focused': { color: '#00C853' } }}>Status</InputLabel>
          <Select
            labelId="status-label"
            id="status"
            name="status"
            value={form.status}
            label="Status"
            onChange={handleChange}
            variant="outlined"
            size="small"
            sx={{
              minHeight: 45,
              height: 45,
              '& .MuiOutlinedInput-root': {
                minHeight: 45,
                height: 45,
                color: '#111',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853 !important' },
              },
              '& label.Mui-focused': { color: '#00C853' },
            }}
            inputProps={{
              sx: {
                background: '#fff',
              },
            }}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      {(editingStoneSize || form.created_by || form.created_at) && (
          <Box sx={{ mt: 2, mb: 1, background: '#f8f9f6', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', p: 2, color: '#222', fontSize: 14, width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#4caf50', marginRight: 6 }}>●</span>
            <span style={{ fontWeight: 600, marginRight: 6 }}>Created by:</span>
            <span style={{ marginRight: 12 }}>{getUserDisplay(editingStoneSize?.created_by || form.created_by)}</span>
              <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}>⏰</span>
            <span>{formatDate(editingStoneSize?.created_at || form.created_at)}</span>
          </div>
          {editingStoneSize && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#ff9800', marginRight: 6 }}>●</span>
              <span style={{ fontWeight: 600, marginRight: 6 }}>Last updated by:</span>
              <span style={{ marginRight: 12 }}>{getUserDisplay(editingStoneSize.updated_by)}</span>
                <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}>⏰</span>
              <span>{formatDate(editingStoneSize.updated_at)}</span>
            </div>
          )}
          </Box>
        )}
        {formError && <Box sx={{ color: '#D32F2F', fontWeight: 500, fontSize: 15 }}>{formError}</Box>}
        {formSuccess && <Box sx={{ color: '#00C853', fontWeight: 500, fontSize: 15 }}>{formSuccess}</Box>}
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button type="submit" fullWidth variant="contained" sx={{ bgcolor: '#00C853', '&:hover': { bgcolor: '#009e46' }, minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1 }} disabled={isSubmitting || codeExists}>
            {editingStoneSize ? 'Update' : 'Add'}
          </Button>
          <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={handleClear} disabled={isSubmitting}>
            Clear
          </Button>
          {editingStoneSize && (
            <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Stone Sizes</DialogTitle>
        <DialogContent>
          {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
          {importSuccess && <Alert severity="success" sx={{ mb: 2 }}>{importSuccess}</Alert>}
          {importedRows.length > 0 && (
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>stone_size_code</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>stone_size_name</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>sieve_size</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>status</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>company_code</th>
                  </tr>
                </thead>
                <tbody>
                  {importedRows.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.stone_size_code}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.stone_size_name}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.sieve_size}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.status}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.company_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
          {importErrors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">Some rows failed to import:</Alert>
              <ul>
                {importErrors.map((err, idx) => (
                  <li key={idx}>{err.error} (Row: {JSON.stringify(err.row)})</li>
                ))}
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button
            onClick={() => setImportDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#757575',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 2,
              minWidth: 110,
              height: 38,
              fontSize: 12,
              letterSpacing: 1,
              boxShadow: 'none',
              mr: 1.5,
              '&:hover': { bgcolor: '#424242' },
            }}
          >
            Cancel
          </Button>
          {!importSuccess && !importError && importedRows.length > 0 && (
            <Button
              onClick={handleConfirmImport}
              variant="contained"
              sx={{
                bgcolor: '#43a047',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 2,
                minWidth: 110,
                height: 38,
                fontSize: 12,
                letterSpacing: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#2e7d32' },
              }}
            >
              Confirm Import
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoneSizeForm; 